import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  PermissionGroupedSchema,
  RoleScopeRowSchema,
  ScopeOwnershipSchema,
  OfferDepartmentSchema,
} from '../schemas/permissions.js';
import { RoleSchema, GetRolesQuerySchema } from '../schemas/roles.js';

export const permissions = {
  /** GET /permissions — returns all permissions grouped by resource (admin-only) */
  getAll: endpoint('GET', '/permissions', z.object({}), PermissionGroupedSchema),

  /** GET /permissions/me — returns effective permission codes for the current user */
  getMyPermissions: endpoint(
    'GET',
    '/permissions/me',
    z.object({}),
    z.object({ permissions: z.array(z.string()) }),
  ),

  /**
   * GET /roles — returns roles (admin-only).
   * Defaults to active-only; pass `includeInactive: true` to also return
   * deactivated roles (e.g. the permission-matrix admin screen).
   */
  getRoles: endpoint('GET', '/roles', GetRolesQuerySchema, z.array(RoleSchema)),

  /** POST /roles — create role (admin only) */
  createRole: endpoint(
    'POST',
    '/roles',
    z.object({ name: z.string().min(1), code: z.string().min(1) }),
    RoleSchema,
  ),

  /** PATCH /roles/:id — update role (admin only) */
  updateRole: endpoint(
    'PATCH',
    '/roles/:id',
    z.object({
      id: z.number(),
      name: z.string().min(1).optional(),
      isActive: z.boolean().optional(),
    }),
    RoleSchema,
  ),

  /** GET /roles/:id/scopes — returns all scope grant rows for a role (admin-only) */
  getRoleScopes: endpoint(
    'GET',
    '/roles/:id/scopes',
    z.object({ id: z.number() }),
    z.array(RoleScopeRowSchema),
  ),

  /**
   * PUT /roles/:id/scopes — replace all scope rows for a role (admin only)
   *
   * 422 Unprocessable Entity guard: the server SHALL reject this request
   * for immutable roles (e.g. 'admin' Power User role) with HTTP 422.
   * The API layer enforces this constraint; no Zod refinement at contract level.
   */
  updateRoleScopes: endpoint(
    'PUT',
    '/roles/:id/scopes',
    z.object({
      id: z.number(),
      scopes: z.array(
        z.object({
          permissionCode: z.string().min(1),
          mediaType: z.string().nullable().optional(),
          ownership: ScopeOwnershipSchema.nullable().optional(),
          department: OfferDepartmentSchema.nullable().optional(),
        }),
      ),
    }),
    z.object({ updated: z.number() }),
  ),
};
